import React, { useCallback, useEffect, useState, useRef } from "react";
import styles from "./style.scss";
import Modal from "components/Common/Modal";
import Button from "components/Common/Button/Text";
import Radio from "components/Common/Radio";
import { CloseConfirmModal } from "features/BinderPage/Common/CloseConfirmModal";
import { trackAction } from "utils/helper/analytics";
import { useCorpImport } from "./hook";

const HELP_URL = "https://intercom.help/valuesearch/ja/articles/4447459";

interface Item {
  companyCode: string;
  companyName: string;
  stkCode: string;
  source: string;
  companyKbn?: string;
  baseOptFlg?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onChange: (corps: Item[]) => void;
  importType: string;
  setImportType: (importType: string) => void;
  binderId: string;
}

export const ImportModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onChange,
  importType,
  setImportType,
  binderId
}) => {
  const { file, isLoading, handleChangeFile, uploadRes } = useCorpImport();

  const inputRef = useRef<HTMLInputElement>(null);
  const IMPORT_DIFF = "0";
  const IMPORT_All = "1";

  const [isSetConfirmModal, setIsSetConfirmModal] = useState(false);
  const [visibleConfirmModal, setVisibleConfirmModal] = useState(false);
  const [selectedCorps, setSelectedCorps] = useState<Item[]>([]);

  const selectedDiff = () => {
    // 新しく作成されたコレクションは、さまざまな更新用に選択できません
    if (!binderId) {
      return;
    }
    setImportType(IMPORT_DIFF);
    if (uploadRes && uploadRes.diffUpdateInfo) {
      setSelectedCorps(
        uploadRes.diffUpdateInfo.corpList.map(item => {
          return {
            companyCode: item.corpCode,
            companyName: item.corpName,
            stkCode: item.stkCode,
            source: item.source,
            companyKbn: item.corpType,
            baseOptFlg: item.baseOptFlg
          };
        })
      );
      trackAction("myCollectionCorpImportRadioChange");
    }
  };

  const selectedAll = useCallback(() => {
    if (uploadRes && uploadRes.allRegistInfo) {
      setImportType(IMPORT_All);
      setSelectedCorps(
        uploadRes.allRegistInfo.corpList.map(item => {
          return {
            companyCode: item.corpCode,
            companyName: item.corpName,
            stkCode: item.stkCode,
            source: item.source,
            companyKbn: item.corpType,
            baseOptFlg: item.baseOptFlg
          };
        })
      );
      // 新しい作成時にAtlasを選択する必要はありません
      if (binderId) {
        trackAction("myCollectionCorpImportRadioChange");
      }
    }
  }, [binderId, uploadRes, setImportType, setSelectedCorps]);

  useEffect(() => {
    // 編集されたコレクションは、初期状態をリセットします
    setImportType("");
    setSelectedCorps([]);
    // 新しく作成されたコレクションの完全な更新を自動的に選択します
    if (!binderId) {
      selectedAll();
    }
  }, [uploadRes, binderId, selectedAll, setImportType, setSelectedCorps]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        title="Excelインポート"
        onClose={() => {
          if (!isOpen) {
            return;
          }

          setVisibleConfirmModal(true);
          return true;
        }}
        uncontrolled
      >
        <div className={styles.container}>
          <div
            className={`${styles.modalBody} ${(selectedCorps.length === 0 ||
              (importType === "0" && uploadRes?.diffUpdateInfo.importMessage) ||
              (importType === "1" && uploadRes?.allRegistInfo.importMessage)) &&
              styles.heightFull}`}
          >
            <div className={styles.selectWrapper}>
              <Button
                className={styles.importButton}
                onClick={() => {
                  inputRef.current?.click();
                  trackAction("myCollectionCorpImportExcelSelect");
                }}
                disabled={isLoading}
              >
                Excelファイルの選択
              </Button>
              <input
                type="file"
                ref={inputRef}
                className={styles.file}
                accept=".xlsx"
                onChange={async e => {
                  await handleChangeFile(e, binderId);
                }}
              />
              <div className={styles.importTitle}>
                <div>ファイル名：</div>
                <div>{file?.name}</div>
              </div>
              <div className={styles.tips}>
                ファイルのフォーマットについては
                <a href={HELP_URL} target="_blank" rel="noopener noreferrer">
                  こちら
                </a>
                をご参照ください。
              </div>
            </div>
            {uploadRes && (
              <div className={styles.uploadInfoWrapper}>
                <div className={styles.importAmount}>
                  アップロードされたファイルから企業データ（
                  {uploadRes.validRecordCount}件）を読み込みました。
                </div>
                {uploadRes.invalidRecordCount > 0 && (
                  <div className={styles.warningMessage}>
                    以下に該当するデータ（{uploadRes.invalidRecordCount}
                    件）が読み込み時に除外されています。
                    <br />
                    ・指定されたコードで対象企業が特定できなかったデータ
                    <br />
                    ・閲覧権限がないデータ
                  </div>
                )}
                {/* [新規コレクション作成]のオプションは表示されません */}
                {binderId && (
                  <>
                    <div className={styles.updateTips}>
                      <div className={styles.title}>更新方法の選択</div>
                      <div className={styles.description}>
                        インポート先のコレクションには既に企業が設定されています。更新方法を選択してください。
                      </div>
                    </div>
                    <div className={styles.updateSelect}>
                      <div className={styles.selectItem} onClick={selectedDiff}>
                        <Radio
                          name="importConditions"
                          isSelected={importType === "0"}
                          uncontrolled
                        />
                        <div>
                          選択済み企業（{uploadRes.registeredCorpCount}
                          件）を残したまま、それらと重複しない企業データ（
                          {uploadRes.diffUpdateInfo.corpCount -
                            uploadRes.registeredCorpCount}
                          件）を追加する
                        </div>
                      </div>
                      <div className={styles.selectItem} onClick={selectedAll}>
                        <Radio
                          name="importConditions"
                          isSelected={importType === "1"}
                          uncontrolled
                        />
                        <div>
                          選択済み企業（{uploadRes.registeredCorpCount}
                          件）をすべて削除してから、読み込んだ企業データ（
                          {uploadRes.allRegistInfo.corpCount}件）を追加する
                        </div>
                      </div>
                      <div className={styles.errorMsg}>
                        {importType === "0" &&
                          uploadRes.diffUpdateInfo.importMessage}
                        {importType === "1" &&
                          uploadRes.allRegistInfo.importMessage}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {selectedCorps.length > 0 &&
              ((importType === "0" &&
                !uploadRes?.diffUpdateInfo.importMessage) ||
                (importType === "1" &&
                  !uploadRes?.allRegistInfo.importMessage)) && (
                <div className={styles.tableWrapper}>
                  <div className={styles.title}>プレビュー</div>
                  <div className={styles.table}>
                    {selectedCorps.map((item, index) => {
                      const disabled = item.baseOptFlg === "9";
                      return (
                        <div
                          className={`${styles.row} ${disabled &&
                            styles.disabled}`}
                          key={`selected-item-row-${index}`}
                        >
                          <span
                            className={`${styles.kabushiki} ${disabled &&
                              styles.disabled}`}
                          >
                            {item.stkCode}
                          </span>
                          <span className={styles.source}>{item.source}</span>
                          <span>{item.companyName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
          </div>
          <div className={styles.buttonContainer}>
            <Button
              onClick={() => {
                // 新しいコレクションを作成するとき、設定パラメーターは値を渡されません。
                // [企業を設定]ボタンをクリックするときは、ATLASを送信します
                trackAction(
                  "myCollectionCorpImportConfirm",
                  binderId ? importType : ""
                );
                // ユーザーは、すべてをインポートするときに求められる必要があります
                if (binderId && importType === "1") {
                  setIsSetConfirmModal(true);
                } else {
                  onChange(selectedCorps);
                }
              }}
              className={styles.button}
              disabled={
                // 次の状況で無効になっています。
                // 1.エンタープライズは選択されていません（更新方法は選択されていません）
                // 2.現在選択されている更新方法にエラーがあります
                selectedCorps.length === 0 ||
                (importType === "0" &&
                  uploadRes?.diffUpdateInfo.importMessage !== "") ||
                (importType === "1" &&
                  uploadRes?.allRegistInfo.importMessage !== "")
              }
            >
              企業を設定
            </Button>
          </div>
        </div>
      </Modal>
      <CloseConfirmModal
        isOpen={visibleConfirmModal}
        keepButtonText="入力を再開"
        onClickKeepButton={() => {
          setVisibleConfirmModal(false);
        }}
        onClickCloseButton={() => {
          setVisibleConfirmModal(false);
          onClose();
        }}
      />

      <CloseConfirmModal
        isOpen={isSetConfirmModal}
        title="更新方法の確認"
        description={`現在登録されている企業をすべて削除してから、読み込んだ企業を\n追加します。\nよろしいですか？`}
        keepButtonText="OK"
        closeButtonText="キャンセル"
        leftButtonColor="navy"
        rightButtonColor="primary"
        onClickKeepButton={() => {
          // [OK]をクリックするときにATLASを送信します
          trackAction(
            "myCollectionCorpImportSecondaryConfirm",
            binderId ? importType : ""
          );
          setIsSetConfirmModal(false);
          onChange(selectedCorps);
        }}
        onClickCloseButton={() => {
          setIsSetConfirmModal(false);
        }}
      />
    </>
  );
};
